import { AirplanIcon } from "@/components/icons/airplane";
import { DeliveryRapid } from "@/components/icons/delivery-rapid";
import { DeliveryTruck } from "@/components/icons/delivery-truck";
import { Location2 } from "@/components/icons/location2";
import { Scale } from "@/components/icons/scale";
import type { ShipmentCalculationResult } from "./types/shipment-calculator.type";
import { useExchangeRate } from "@/hooks/useExchangeRate";
import { ArrowLeft, Info } from "lucide-react";
import { ApproxEqualsIcon } from "@/components/icons/ApproxEqualsIcon";
import { cn } from "@/lib/utils";


type CostRange = {
  min: number;
  max: number;
};

type CostItem = {
  key: string;
  label: string;
  description: string;
  color: string;
  single: number;
  range?: CostRange;
};

const COST_COLORS = {
  shipping: "#063982",
  clearance: "#8CA4C6",
  processing: "#587AAB",
};

function isRangedResult(
  results: ShipmentCalculationResult,
): results is ShipmentCalculationResult & {
  processing_fee_2: number;
  vat_2: number;
  int_shipping_fee_2: number;
  clearance_cost_2: number;
} {
  return "int_shipping_fee_2" in results;
}

function sortRange(value1: number, value2: number): CostRange {
  return {
    min: Math.min(value1, value2),
    max: Math.max(value1, value2),
  };
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatLocalCurrency(value: number, currency: string) {
  return new Intl.NumberFormat(currency === "GHS" ? "en-GH" : "en-NG", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

function formatRange(range: CostRange, formatter: (value: number) => string) {
  if (range.min.toFixed(2) === range.max.toFixed(2)) return formatter(range.min);
  return `${formatter(range.min)} - ${formatter(range.max)}`;
}

function getCountryName(country: ShipmentCalculationResult["country"]) {
  return country === "GH" ? "Ghana" : "Nigeria";
}

function getCosts(results: ShipmentCalculationResult) {
  const processing1 = results.processing_fee_1 + results.vat_1;
  const items: CostItem[] = [
    {
      key: "shipping",
      label: "Shipping Cost",
      description: results.method === "Air" ? "Air Freight Charges" : "Sea Freight Charges",
      color: COST_COLORS.shipping,
      single: results.int_shipping_fee_1,
      range: isRangedResult(results)
        ? sortRange(results.int_shipping_fee_1, results.int_shipping_fee_2)
        : undefined,
    },
    {
      key: "clearance",
      label: "Clearance Cost included",
      description: "Custom clearance & documentation",
      color: COST_COLORS.clearance,
      single: results.clearance_cost_1,
      range: isRangedResult(results)
        ? sortRange(results.clearance_cost_1, results.clearance_cost_2)
        : undefined,
    },
    {
      key: "processing",
      label: "Processing Fee included",
      description: "Based on weight/CBM",
      color: COST_COLORS.processing,
      single: processing1,
      range: isRangedResult(results)
        ? sortRange(processing1, results.processing_fee_2 + results.vat_2)
        : undefined,
    },
  ];

  const totalSingle = items.reduce((sum, item) => sum + item.single, 0);
  const totalRange = isRangedResult(results)
    ? sortRange(
        results.int_shipping_fee_1 + results.clearance_cost_1 + processing1,
        results.int_shipping_fee_2 +
          results.clearance_cost_2 +
          results.processing_fee_2 +
          results.vat_2,
      )
    : undefined;

  return { items, totalSingle, totalRange };
}

function getSummaryMeasurement(results: ShipmentCalculationResult) {
  if (
    typeof results.estimated_weight_range?.min === "number" &&
    typeof results.estimated_weight_range.max === "number"
  ) {
    const estimatedWeightRange = sortRange(
      results.estimated_weight_range.min,
      results.estimated_weight_range.max,
    );

    return {
      label: "Estimated Weight",
      value: formatRange(estimatedWeightRange, (value) => `${value} kg`),
    };
  }

  if (results.estimated_weight) {
    return {
      label: "Estimated CBM",
      value: `${results.estimated_weight} cbm`,
    };
  }

  if (results.method === "Sea") {
    return {
      label: "CBM",
      value: results.cbm ? `${results.cbm} cbm` : "-",
    };
  }

  return {
    label: "Weight",
    value: results.weight ? `${results.weight} kg` : "-",
  };
}

function CalculationResults({
  results,
  setStep,
}: {
  results: ShipmentCalculationResult | null;
  setStep?: React.Dispatch<React.SetStateAction<number>>;
}) {
  const targetCurrency = results?.country === "GH" ? "GHS" : "NGN";
  const { data: exchangeRateData } = useExchangeRate("USD", targetCurrency, !!results);
  const rawRate = exchangeRateData?.data?.rate ?? (results?.country === "GH" ? 15.0 : 1500.0);
  const exchangeRateFactor = rawRate < 1 ? 1 / rawRate : rawRate;

  if (!results) {
    return <div className="text-center text-base text-primary-text-600">No result available.</div>;
  }

  const { items, totalSingle, totalRange } = getCosts(results);
  const displayTotal = totalRange
    ? formatRange(totalRange, formatCurrency)
    : formatCurrency(totalSingle);

  const localMinVal = totalRange ? Math.round(totalRange.min * exchangeRateFactor) : Math.round(totalSingle * exchangeRateFactor);
  const localMaxVal = totalRange ? Math.round(totalRange.max * exchangeRateFactor) : null;

  const formattedLocalMin = formatLocalCurrency(localMinVal, targetCurrency);
  const formattedLocalMax = localMaxVal !== null ? formatLocalCurrency(localMaxVal, targetCurrency) : null;

  const displayLocalText = formattedLocalMax !== null
    ? `${formattedLocalMin} - ${formattedLocalMax}`
    : formattedLocalMin;

  const measurement = getSummaryMeasurement(results);

  const summaryRows = [
    {
      label: "Shipping Type",
      value: results.method,
      icon: DeliveryTruck,
    },
    ...(results.method === "Air"
      ? [
          {
            label: "Shipment Type",
            value: results.kind || "-",
            icon: AirplanIcon,
          },
        ]
      : []),
    {
      label: "Destination",
      value: getCountryName(results.country),
      icon: Location2,
    },
    {
      label: measurement.label,
      value: measurement.value,
      icon: Scale,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-[1000px] text-[#1A202C]">
      {setStep && (
        <button
          type="button"
          className="mb-[17px] flex items-center gap-2 text-sm font-normal text-[#4A5568] hover:text-[#38619B] transition-colors"
          onClick={() => setStep(1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to calculator
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Row 1, Col 1 — Estimated Shipping Cost */}
        <section className="rounded-[13px] bg-[#003380] border border-blue-900/20 px-6 py-7 text-white sm:px-10 sm:py-7 flex flex-col justify-between h-full min-h-[220px] lg:w-[479px] shadow-sm">
          <div>
            <p className="text-base font-normal sm:text-lg">Estimated Shipping Cost</p>
            <p className="mt-2 text-3xl font-normal leading-tight sm:text-4xl">{displayTotal}</p>
          </div>
          
          <div className="my-5 border-t border-white w-full" />
          
          <div className="flex items-center gap-3">
            <span className={cn(
              "flex shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-black",
              targetCurrency === "GHS" ? "h-8 w-8" : "h-[19px] w-[19px]"
            )}>
              {targetCurrency === "GHS" ? "GH₵" : "₦"}
            </span>
            <ApproxEqualsIcon className="h-4 w-4 text-white shrink-0" />
            <p className="text-xl font-normal sm:text-2xl">{displayLocalText}</p>
          </div>

          <div className="mt-6 flex flex-col items-start gap-1">
            <span className="inline-flex rounded-full bg-white px-4 py-2 text-xs font-normal text-[#645E82]">
              All inclusive
            </span>
            <p className="text-xs font-medium sm:text-sm text-white">
              Includes shipping, clearance and processing fee
            </p>
          </div>
        </section>

        {/* Row 1, Col 2 — Shipping Summary (mobile: last, desktop: second) */}
        <section className="order-last lg:order-none flex flex-col h-full border-t lg:border-t-0 pt-6 lg:pt-0">
          <div>
            <h2 className="text-xl font-semibold text-[#161618] mb-6">Shipping Summary</h2>
            <div className="mt-6 space-y-5">
              {summaryRows.map(({ icon: Icon, label, value }) => (
                <div key={label} className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
                  <Icon className="h-5 w-5 text-[#1A202C]" />
                  <p className="text-base font-normal text-[#202023]">{label}</p>
                  <p className="text-base font-medium text-[#1A202C]">{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3 rounded-lg border border-[#38619B] px-4 py-4 text-xs text-[#38619B] shadow-sm">
            <DeliveryRapid className="h-5 w-5 shrink-0 text-[#38619B]" aria-hidden="true" />
            <p className="font-normal leading-[120%]">
              Clearance is included in your total cost. We handle all customs & documentation for
              you.
            </p>
          </div>
        </section>

        {/* Row 2, Col 1 — Cost Breakdown */}
        <section className="border-t lg:border-t-0 bg-[#E6EBF34D] rounded-md px-4 pt-6 pb-0 sm:px-3 sm:py-2">
          <div className="border-b border-slate-200 pb-2 grid grid-cols-[1.8fr_1.1fr_1.1fr] gap-4 text-xs font-bold uppercase text-[#4A5568]">
            <span className="text-sm sm:text-base font-medium text-[#161618] text-left">Cost Breakdown</span>
            <span className="text-[#A0A0A6] text-center pt-[5px]">USD</span>
            <span className="text-[#A0A0A6] text-center pt-[5px]">{targetCurrency === "GHS" ? "GHS" : "Naira"}</span>
          </div>

          <div className="mt-4 space-y-4">
            {items.map((item) => {
              const usdValue = formatCurrency(item.single);
              const localValue = formatLocalCurrency(item.single * exchangeRateFactor, targetCurrency);

              return (
                <div key={item.key} className="grid grid-cols-[1.8fr_1.1fr_1.1fr] gap-4 border-b border-[#0000001A] pb-4 items-center">
                  <div className="text-left flex flex-col items-start">
                    <p className="text-sm font-normal text-[#202023]">{item.label}</p>
                    <p className="text-xs text-[#505057]">{item.description}</p>
                  </div>
                  
                  <p className="text-center flex justify-center items-center text-sm font-normal text-[#0A0A0A]">
                    {usdValue}
                  </p>

                  <p className="text-center flex justify-center items-center text-sm font-normal text-[#0A0A0A]">
                    {localValue}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="pt-5">
            <div className="flex items-start justify-between w-full sm:block">
              <p className="text-base font-normal text-[#36363A] sm:hidden">Total</p>

              {totalRange ? (
                <div className="flex flex-col items-start ml-auto sm:hidden">
                  <div className="flex items-center">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-normal text-[#063982] uppercase leading-none">MIN</span>
                      <span className="text-sm font-medium text-[#063982] mt-1">{formatCurrency(totalRange.min)}</span>
                    </div>
                    <div className="w-3 border-t border-slate-300 mx-2 mt-4" />
                    <div className="flex flex-col">
                      <span className="text-[10px] font-normal text-[#063982] uppercase leading-none">MAX</span>
                      <span className="text-sm font-medium text-[#063982] mt-1">{formatCurrency(totalRange.max)}</span>
                    </div>
                  </div>
                  <ApproxEqualsIcon className="h-3.5 w-3.5 text-[#063982] my-1 pl-0.5" aria-hidden="true" />
                  <div className="flex items-center">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-normal text-[#063982] uppercase leading-none">MIN</span>
                      <span className="text-sm font-medium text-[#063982] mt-1">{formatLocalCurrency(totalRange.min * exchangeRateFactor, targetCurrency)}</span>
                    </div>
                    <div className="w-3 border-t border-slate-300 mx-2 mt-4" />
                    <div className="flex flex-col">
                      <span className="text-[10px] font-normal text-[#063982] uppercase leading-none">MAX</span>
                      <span className="text-sm font-medium text-[#063982] mt-1">{formatLocalCurrency(totalRange.max * exchangeRateFactor, targetCurrency)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-start ml-auto sm:hidden">
                  <span className="text-sm font-medium text-[#063982]">{formatCurrency(totalSingle)}</span>
                  <ApproxEqualsIcon className="h-3.5 w-3.5 text-[#063982] my-1 pl-0.5" aria-hidden="true" />
                  <span className="text-sm font-medium text-[#063982]">{formatLocalCurrency(totalSingle * exchangeRateFactor, targetCurrency)}</span>
                </div>
              )}

              <div className="hidden sm:grid sm:grid-cols-[1.8fr_1.1fr_1.1fr] sm:gap-4 sm:items-center">
                <p className="text-base font-normal text-[#36363A] text-left">Total</p>
                <div className="flex items-center gap-1 justify-center">
                  {totalRange ? (
                    <div className="flex items-end gap-1.5 justify-center text-sm font-medium text-[#063982]">
                      <div className="flex flex-col items-left">
                        <span className="text-[10px] font-normal text-[#063982] uppercase leading-none mb-1">MIN</span>
                        <span>{formatCurrency(totalRange.min)}</span>
                      </div>
                      <span className="text-slate-400 mx-1 pb-3">—</span>
                      <div className="flex flex-col items-left">
                        <span className="text-[10px] font-normal text-[#063982] uppercase leading-none mb-1">MAX</span>
                        <span>{formatCurrency(totalRange.max)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-normal text-[#063982] text-center">{formatCurrency(totalSingle)}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 justify-center">
                  {totalRange ? (
                    <div className="flex items-end gap-1 justify-center text-sm font-medium text-[#063982]">
                      <ApproxEqualsIcon className="h-3.5 w-3.5 text-[#063982] shrink-0 mb-0.5" />
                      <div className="flex flex-col items-left">
                        <span className="text-[10px] font-normal text-[#063982] uppercase leading-none mb-1">MIN</span>
                        <span>{formatLocalCurrency(totalRange.min * exchangeRateFactor, targetCurrency)}</span>
                      </div>
                      <span className="text-slate-400 mx-1 pb-3">—</span>
                      <div className="flex flex-col items-left">
                        <span className="text-[10px] font-normal text-[#063982] uppercase leading-none mb-1">MAX</span>
                        <span>{formatLocalCurrency(totalRange.max * exchangeRateFactor, targetCurrency)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 justify-center text-sm font-medium text-[#063982] text-center">
                      <span>{formatLocalCurrency(totalSingle * exchangeRateFactor, targetCurrency)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <p className="mt-5 text-xs font-normal text-[#063982] flex items-start gap-1.5">
            <Info className="sm:hidden h-3.5 w-3.5 shrink-0 mt-[1px] text-[#063982]" aria-hidden="true" />
            The local currency value is based on today&apos;s exchange rate and is subject to change
          </p>
        </section>

        {/* Row 2, Col 2 — empty */}
      </div>
    </div>
  );
}

export default CalculationResults;
